/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import styled, { css } from "styled-components";


const enum fields {
    columns = "columns",
    gap = "gap",
    resizeRows = "resizeRows",
    collapseOnMobile = "collapseOnMobile",
    reverseOrderOnMobile = "reverseOrderOnMobile",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.resizeRows]: boolean;
        [fields.collapseOnMobile]: boolean;
        [fields.reverseOrderOnMobile]: boolean;
    };
}

const Container = styled('div')<{gap: number, collapse: boolean, reverse: boolean}>`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: ${p => `${p.gap  }px`};
    ${({ theme, reverse }) => 
        (breakpointMediaQueries(theme, 
        [css`flex-direction: ${reverse ? 'column-reverse' : 'column'};`,
        css`flex-direction:  ${reverse ? 'column-reverse' : 'column'};`]))};
    ${({ theme }: { theme: BaseTheme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map(mw =>
                mw
                    ? css`
                            max-width: ${mw}px;
                            margin: 0 auto;
                        `
                    : null,
            ),
        );
    }};
    ${p => {
        if (!p.collapse) {
            return css`
                flex-direction: row !important;
            `;
        }
    }}
`;

const RowItem = styled('div')<{width: number, resize: boolean}>`
    ${p => {
        if (!p.resize) {
            return css`
                width: ${`${p.width  }%`};
            `;
        } 
            return css`
                display: inline-flex;
                flex-basis: ${`${p.width  }%`};
                flex-grow: 1;
                flex-shrink: 1;
            `;
        
    }}
    ${({ theme }) => 
        (breakpointMediaQueries(theme, 
        [css`width: 100%;`,
        css`width:100%;`]))};

    > div {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
`;

const StoryRow: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, resizeRows, collapseOnMobile, reverseOrderOnMobile },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        columnElements.push(
            <RowItem key={x} width={(columns[x]/12)*100} resize={resizeRows}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </RowItem>,
        );
    }

    return <Container collapse={collapseOnMobile === undefined ? true : collapseOnMobile} gap={gap || 0} reverse={reverseOrderOnMobile}>{columnElements}</Container>;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: StoryRow,
    definition: {
        group: "Basic",
        icon: "Row",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.resizeRows,
                displayName: "Resize rows based on content",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General"
            },
            {
                name: fields.collapseOnMobile,
                displayName: "Collapse row on mobile",
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            },
            {
                name: fields.reverseOrderOnMobile,
                displayName: "Reverse order on mobile",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            }
        ],
    },
};

export default widgetModule;
